import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Send, X } from '@/components/icons';
import { handoffToModule, type HandoffPayload, type HandoffResult } from '@/api/notifications';
import { apiError } from '@/lib/apiError';
import { Button } from './Button';
import { useToast } from './useToast';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Human-readable action verb ("record a GRN", "approve this PR") for the modal copy. */
  action: string;
  /** Payload forwarded to the handoff endpoint. Fires when the user clicks Confirm. */
  payload: HandoffPayload;
  /** Called after a successful handoff, e.g. to close a parent context. */
  onSent?: (result: HandoffResult) => void;
}

/**
 * Confirmation dialog shown when a user without permission on a target
 * module clicks a cross-module action. Explains what will happen and
 * forwards an in-app notification to the module's permission holders.
 */
export function HandoffModal({ open, onClose, action, payload, onSent }: Props) {
  const toast = useToast();
  const sendMutation = useMutation({
    mutationFn: () => handoffToModule(payload),
    onSuccess: (res) => {
      if (res.recipients === 0) {
        toast.push(
          `No user currently has permission on ${res.moduleLabel ?? 'the target module'}. Please ask an admin to assign it.`,
          'warning',
        );
        onClose();
        return;
      }
      const who = res.roleNames.length
        ? res.roleNames.join(', ')
        : `${res.recipients} user${res.recipients === 1 ? '' : 's'}`;
      toast.pushStep(`Request forwarded to ${who}`, {
        body: `They will ${action} on your behalf and you'll see the update once it's done.`,
        tag: `handoff-${payload.moduleId}-${payload.entityId ?? 'nil'}`,
      });
      onSent?.(res);
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Could not forward the request'), 'error');
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]">
            <AlertTriangle size={16} className="text-warning" /> You don&rsquo;t own this step
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mb-2 text-xs text-[var(--text-secondary)]">
          You don&rsquo;t have permission to <strong>{action}</strong>. If you continue, we&rsquo;ll
          send an in-app request to every teammate whose role owns that module — they&rsquo;ll pick
          it up from their notification bell.
        </p>
        <div className="mt-3 rounded-md border border-info/30 bg-info/[0.05] px-3 py-2 text-[11px] text-[var(--text-secondary)]">
          <div className="font-bold text-[var(--text-primary)]">{payload.subject}</div>
          <div className="mt-0.5">{payload.body}</div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="glass" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => sendMutation.mutate()}
            disabled={sendMutation.isPending}
          >
            <span className="inline-flex items-center gap-1">
              <Send size={12} />
              {sendMutation.isPending ? 'Sending…' : 'Forward request'}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}
